import React, {useEffect, useState, useCallback, memo} from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import BottomSheet, { BottomSheetScrollView, BottomSheetTextInput, BottomSheetView } from '@gorhom/bottom-sheet';
import StarRating from 'react-native-star-rating-widget';
import {useTheme} from '~contexts/ThemeContext';
import {useUserStore} from '~providers/userStore/zustand';
import FastImage from 'react-native-fast-image';
import {useGetEventGroupingAttendees} from '~hooks/event/useGetEventGroupingAttendees';

interface Question {
  id: string;
  question: string;
  type: 'rating' | 'select' | 'input' | 'multi-select' | 'attendees_rating';
  options?: string[];
}

interface AttendeeCardProps {
  attendee: any;
  currentRating?: string;
  onRate: (rating: string) => void;
  colors: any;
  styles: any;
}

const AttendeeRatingCard = memo(({ attendee, currentRating, onRate, colors, styles }: AttendeeCardProps) => {
  const name = attendee.first_name || (attendee.user?.first_name ? `${attendee.user.first_name} ${attendee.user.last_name || ''}` : attendee.name);
  
  const ratingOptions = [
    { label: 'Very Poor', emoji: '😫', color: '#EF4444' },
    { label: 'Poor', emoji: '😕', color: '#F59E0B' },
    { label: 'Neutral', emoji: '😐', color: '#FCD34D' },
    { label: 'Good', emoji: '🙂', color: '#34D399' },
    { label: 'Excellent', emoji: '😁', color: '#10B981' },
  ];

  return (
    <View style={styles.attendeeCard}>
      <View style={styles.attendeeInfo}>
        <FastImage 
          source={attendee.photo ? { uri: attendee.photo } : (attendee.user?.photo ? { uri: attendee.user?.photo } : require('~assets/images/default_avatar.png'))} 
          style={styles.attendeeAvatar} 
        />
        <View style={styles.attendeeDetails}>
          <Text style={[styles.attendeeName, {color: colors.textPrimary}]}>{name}</Text>
          {false && <View style={styles.attendeeSubInfo}>
            <Text style={styles.socialPulseText}>
              <Text style={{color: '#10B981', fontWeight: '700'}}>
                {attendee.social_pulse || attendee.user?.social_pulse || 75} Social Pulse
              </Text>
              {'  •  '}
              {attendee.subscription_type || attendee.user?.subscription_type || 'Monthly'}
            </Text>
          </View>}
        </View>
      </View>

      <View style={styles.ratingButtonsContainer}>
        {ratingOptions.map((opt) => (
          <TouchableOpacity
            key={opt.label}
            onPress={() => onRate(opt.label)}
            style={[
              styles.ratingOption,
              {
                backgroundColor: currentRating === opt.label ? opt.color + '20' : colors.gray100 + '50',
                borderColor: currentRating === opt.label ? opt.color : 'transparent',
              }
            ]}>
            <Text style={styles.ratingEmoji}>{opt.emoji}</Text>
            <Text style={[
              styles.ratingLabel,
              { color: currentRating === opt.label ? opt.color : colors.textSecondary }
            ]}>
              {opt.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <TouchableOpacity
        onPress={() => onRate('Didn\'t Show Up')}
        style={[
          styles.didntShowButton,
          {
            backgroundColor: currentRating === 'Didn\'t Show Up' ? colors.gray300 : colors.gray100 + '80',
          }
        ]}>
        <Text style={[
          styles.didntShowText,
          { color: currentRating === 'Didn\'t Show Up' ? colors.textPrimary : colors.textSecondary }
        ]}>
          Didn't Show Up
        </Text>
      </TouchableOpacity>
    </View>
  );
});

interface FlexibleReviewSheetProps {
  bottomSheetRef: any;
  eventType: string;
  onSubmit: (metadata: Record<string, any>) => void;
  event_id?: number;
  attendees?: any[];
}

const QUESTIONS_CONFIG: Record<string, Question[]> = {
  CULTURAL_EDIT: [
    {
      id: 'overall_rating',
      question: 'Overall, how would you rate the event?',
      type: 'rating'
    },
    {
      id: 'companions_rating',
      question: 'How would you rate your companions in terms of compatibility and conversation quality?',
      type: 'rating'
    },
    {
      id: 'attendee_connections',
      question: 'How was your connection with each person? Your feedback helps us create better matches!',
      type: 'attendees_rating'
    },
    {
      id: 'bring_person',
      question: 'Is there a person in your group, you\'d bring to Pyxi\'s +1 events next time?',
      type: 'input'
    },
    {
      id: 'recommend_friend',
      question: 'Would you recommend Pyxi events to a friend?',
      type: 'select',
      options: ['Yes', 'No', 'Maybe']
    },
    {
      id: 'attend_another',
      question: 'How likely are you to attend another Pyxi event?',
      type: 'rating'
    },
    {
      id: 'meet_group_again',
      question: 'Would you like to meet this group again?',
      type: 'select',
      options: [
        'Yes – same group, same kind of activity.',
        'Yes – same group, but a different activity.',
        'Yes – but with a slightly different group mix.',
        'No – I\'d prefer not to meet this group again.'
      ]
    },
    {
      id: 'specific_experience',
      question: 'Is there anything specific you\'d like to share about your experience?',
      type: 'input'
    },
    {
      id: 'postcode',
      question: 'Enter your post code and we\'ll let you know when your Pyxi Neighbourhood is ready 🙂',
      type: 'input'
    }
  ],
  HOUSEWARMING: [
    {
      id: 'atmosphere_rating',
      question: 'How would you describe the atmosphere and vibe of the night?',
      type: 'rating'
    },
    {
      id: 'connect_people',
      question: 'Do you feel that you were able to connect with new people tonight?',
      type: 'select',
      options: ['Yes', 'Somewhat', 'Not really']
    },
    {
      id: 'attendee_connections',
      question: 'How was your connection with each person? Your feedback helps us create better matches!',
      type: 'attendees_rating'
    },
    {
      id: 'favourite_moment',
      question: 'What was your favourite moment of the night?',
      type: 'input'
    },
    {
      id: 'recommend_friend',
      question: 'Would you recommend Pyxi events to a friend?',
      type: 'select',
      options: ['Yes', 'No', 'Maybe']
    },
    {
      id: 'experience_type',
      question: 'What type of experiences would you like to see more of?',
      type: 'multi-select',
      options: [
        'More housewarming parties',
        'Pyxi dinners',
        'Daytime meetups',
        'Cultural or creative events',
        'Outdoor or active experiences',
        'Smaller themes gatherings'
      ]
    },
    {
      id: 'feedback',
      question: 'Any feedback, ideas or suggestions for us?',
      type: 'input'
    },
    {
      id: 'neighbourhood_postcode',
      question: 'If you are interested in our new Neighbourhoods offering (helping you connect with your neighbours 🏡✨), please enter your postcode below.',
      type: 'input'
    }
  ],
  THE_REST_TABLE: [
    {
      id: 'dinner_rating',
      question: 'Overall, how would you rate the dinner event?',
      type: 'rating'
    },
    {
      id: 'companions_rating',
      question: 'How would you rate your dinner companions in terms of compatibility and conversation quality?',
      type: 'rating'
    },
    {
      id: 'attendee_connections',
      question: 'How was your connection with each person? Your feedback helps us create better matches!',
      type: 'attendees_rating'
    },
    {
      id: 'bring_person',
      question: 'Is there a person in your group, you\'d bring to Pyxi\'s +1 dinners, next time?',
      type: 'input'
    },
    {
      id: 'recommend_dinners',
      question: 'Would you recommend Pyxi dinners to a friend?',
      type: 'select',
      options: ['Yes', 'No', 'Maybe']
    },
    {
      id: 'attend_another',
      question: 'How likely are you to attend another Pyxi dinner?',
      type: 'rating'
    },
    {
      id: 'meet_group_again',
      question: 'Would you like to meet this group again?',
      type: 'select',
      options: [
        'Yes – same group, same kind of activity.',
        'Yes – same group, but a different activity.',
        'Yes – but with a slightly different group mix.',
        'No – I\'d prefer not to meet this group again.'
      ]
    },
    {
      id: 'specific_experience',
      question: 'Is there anything specific you\'d like to share about your experience?',
      type: 'input'
    },
    {
      id: 'postcode',
      question: 'Enter your post code and we\'ll let you know when your Pyxi Neighbourhood is ready 🙂',
      type: 'input'
    }
  ]
};

const FlexibleReviewSheet: React.FC<FlexibleReviewSheetProps> = ({
  bottomSheetRef,
  eventType,
  onSubmit,
  event_id,
  attendees: propAttendees = [],
}) => {
  const {colors} = useTheme();
  const {user} = useUserStore();
  const {data: fetchedAttendees} = useGetEventGroupingAttendees({event_id: event_id || 0});
  const attendees = event_id && fetchedAttendees ? fetchedAttendees : propAttendees;
  const snapPoints = ['85%'];
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [currentStep, setCurrentStep] = useState(0);

  console.log(fetchedAttendees, event_id, 'fetchedAttendeesfetchedAttendees');
  
  
  const questions = QUESTIONS_CONFIG[eventType] || QUESTIONS_CONFIG[eventType === 'RESET_TABLE' ? 'THE_REST_TABLE' : ''] || [];

  useEffect(() => {
    // Initialize answers with user email if available
    if (user?.email) {
      setAnswers(prev => ({
        ...prev,
        email: user.email
      }));
    }
  }, [user?.email]);

  const onSubmitPress = () => {
    // Define which questions should have asterisks based on event type
    const getFormattedQuestion = (question: Question, eventType: string) => {
      const questionText = question.question;
      
      if (eventType === 'CULTURAL_EDIT') {
        if (questionText.includes('Overall, how would you rate') || 
            questionText.includes('How would you rate your companions') ||
            questionText.includes('Would you recommend Pyxi events') ||
            questionText.includes('How likely are you to attend') ||
            questionText.includes('Is there anything specific')) {
          return `*${questionText}*`;
        }
      } else {
        // Default asterisk logic for other event types including HOUSEWARMING and THE_REST_TABLE
        if (questionText.includes('Overall, how would you rate') ||
            questionText.includes('Would you recommend Pyxi') ||
            questionText.includes('How likely are you to attend') ||
            questionText.includes('How would you describe the atmosphere') ||
            questionText.includes('Do you feel that you were able to connect') ||
            questionText.includes('What was your favourite moment') ||
            questionText.includes('What type of experiences') ||
            questionText.includes('Any feedback, ideas')) {
          return `*${questionText}*`;
        }
      }
      
      return questionText;
    };

    const metadata: Record<string, any> = {
      'email': user?.email || '',
    };

    questions.forEach(q => {
      if (q.type === 'attendees_rating') {
        const attendeeRatings = answers[q.id];
        if (attendeeRatings && typeof attendeeRatings === 'object') {
          const ratingMap: Record<string, number> = {
            'Very Poor': 1,
            'Poor': 2,
            'Neutral': 3,
            'Good': 4,
            'Excellent': 5,
            "Didn't Show Up": 0
          };

          metadata['attenddes'] = Object.entries(attendeeRatings).map(([id, rating]) => {
            const attendee = attendees.find(a => (a.user?.uid || a.user_id) === id);
            const name = attendee?.user ? `${attendee.user.first_name} ${attendee.user.last_name || ''}` : (attendee?.name || id);
            return {
              email: attendee?.user?.email || attendee?.email || id,
              rating: ratingMap[rating as string] || (typeof rating === 'number' ? rating : parseInt(rating as string, 10)) || 0,
              name: name
            };
          });
        }
      } else {
        let value = answers[q.id];
        
        // Format multi-select as comma separated string
        if (q.type === 'multi-select' && Array.isArray(value)) {
          value = value.join(', ');
        } else if (value === undefined) {
          value = q.type === 'rating' ? '0' : '';
        }
        
        // Convert to string to match requested format
        const finalValue = value?.toString() || '';
        
        metadata[getFormattedQuestion(q, eventType)] = finalValue;
      }
    });
    
    onSubmit(metadata);
  };

  const onSheetClose = () => {
    bottomSheetRef.current?.close();
  };

  const updateAnswer = useCallback((questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  const renderQuestion = (question: Question, index: number) => {
    const answer = answers[question.id];

    switch (question.type) {
      case 'attendees_rating':
        const otherAttendees = attendees.filter(a => a.user?.uid !== user?.uid);
        
        if (otherAttendees.length === 0) {
          return (
            <View key={question.id} style={styles.questionContainer}>
              <Text style={[styles.questionText, {color: colors.textPrimary}]}>
                No other attendees to rate.
              </Text>
            </View>
          );
        }

        return (
          <View key={question.id} style={styles.attendeesQuestionContainer}>
            <View style={styles.headerBanner}>
              <Text style={styles.headerBannerText}>
                ✨ {question.question}
              </Text>
            </View>
            
            {otherAttendees.map((attendee, idx) => {
              const attendeeId = attendee.user?.uid || attendee.user_id;
              
              const handleRate = (rating: string) => {
                const currentAttendeeRatings = answers[question.id] || {};
                const newAttendeeRatings = { ...currentAttendeeRatings, [attendeeId]: rating };
                updateAnswer(question.id, newAttendeeRatings);
              };

              return (
                <AttendeeRatingCard 
                  key={attendeeId}
                  attendee={attendee}
                  currentRating={answer?.[attendeeId]}
                  onRate={handleRate}
                  colors={colors}
                  styles={styles}
                />
              );
            })}
          </View>
        );

      case 'rating':
        return (
          <View key={question.id} style={styles.questionContainer}>
            <Text style={[styles.questionText, {color: colors.textPrimary}]}>
              {question.question}
            </Text>
            <StarRating
              rating={answer || 0}
              onChange={(value) => updateAnswer(question.id, value)}
              color={colors.primary}
              emptyColor={colors.gray300}
              starSize={30}
              style={styles.ratingBar}
            />
          </View>
        );

      case 'select':
        return (
          <View key={question.id} style={styles.questionContainer}>
            <Text style={[styles.questionText, {color: colors.textPrimary}]}>
              {question.question}
            </Text>
            <View style={styles.optionsContainer}>
              {question.options?.map((option, optIndex) => (
                <TouchableOpacity
                  key={optIndex}
                  style={[
                    styles.optionButton,
                    {
                      backgroundColor: answer === option ? colors.primary : colors.white,
                      borderColor: answer === option ? colors.primary : colors.border,
                    }
                  ]}
                  onPress={() => updateAnswer(question.id, option)}>
                  <Text style={[
                    styles.optionText,
                    {
                      color: answer === option ? colors.white : colors.textPrimary,
                    }
                  ]}>
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        );
      case 'multi-select':
        return (
          <View key={question.id} style={styles.questionContainer}>
            <Text style={[styles.questionText, {color: colors.textPrimary}]}>
              {question.question}
            </Text>
            <View style={styles.optionsContainer}>
              {question.options?.map((option, optIndex) => {
                const isSelected = Array.isArray(answer) && answer.includes(option);
                return (
                  <TouchableOpacity
                    key={optIndex}
                    style={[
                      styles.optionButton,
                      {
                        backgroundColor: isSelected ? colors.primary : colors.white,
                        borderColor: isSelected ? colors.primary : colors.border,
                      }
                    ]}
                    onPress={() => {
                      const currentAnswers = Array.isArray(answer) ? [...answer] : [];
                      if (isSelected) {
                        updateAnswer(question.id, currentAnswers.filter(item => item !== option));
                      } else {
                        updateAnswer(question.id, [...currentAnswers, option]);
                      }
                    }}>
                    <Text style={[
                      styles.optionText,
                      {
                        color: isSelected ? colors.white : colors.textPrimary,
                      }
                    ]}>
                      {option}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        );
      case 'input':
        return (
          <View key={question.id} style={styles.questionContainer}>
            <Text style={[styles.questionText, {color: colors.textPrimary}]}>
              {question.question}
            </Text>
            <BottomSheetTextInput
              placeholder="Enter your answer"
              style={[styles.answerInput, {borderColor: colors.border, color: colors.textPrimary}]}
              multiline={true}
              onChangeText={(value) => updateAnswer(question.id, value)}
              value={answer || ''}
              placeholderTextColor={colors.statusGray}
            />
          </View>
        );

      default:
        return null;
    }
  };

  const isFormValid = () => {
    // Check if we're on the last step and have answered the current question
    const currentQuestion = questions[currentStep];
    if (!currentQuestion) return false;
    
    const answer = answers[currentQuestion.id];
    
    if (currentQuestion.type === 'multi-select') {
      return Array.isArray(answer) && answer.length > 0;
    }

    if (currentQuestion.type === 'attendees_rating') {
      const otherAttendees = attendees.filter(a => a.user?.uid !== user?.uid);
      if (otherAttendees.length === 0) return true;
      
      // Check if every attendee has a rating
      return otherAttendees.every(attendee => {
        const attendeeId = attendee.user?.uid || attendee.user_id;
        return answer?.[attendeeId] !== undefined;
      });
    }
    
    const hasCurrentAnswer = answer && 
      (currentQuestion.type !== 'rating' || answer > 0);
    
    return hasCurrentAnswer;
  };

  const completedQuestions = questions.filter(q => {
    const answer = answers[q.id];
    if (q.type === 'rating') {
      return answer > 0;
    }
    if (q.type === 'multi-select') {
      return Array.isArray(answer) && answer.length > 0;
    }
    if (q.type === 'attendees_rating') {
      const otherAttendees = attendees.filter(a => a.user?.uid !== user?.uid);
      if (otherAttendees.length === 0) return true;
      return otherAttendees.every(attendee => {
        const attendeeId = attendee.user?.uid || attendee.user_id;
        return answer?.[attendeeId] !== undefined;
      });
    }
    return answer && answer.toString().trim() !== '';
  }).length;

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    headerContainer: {
      padding: 20,
      paddingBottom: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border + '30',
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: '700',
      color: colors.textPrimary,
      textAlign: 'center',
      marginBottom: 8,
    },
    progressContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    progressText: {
      fontSize: 14,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    progressBar: {
      height: 4,
      backgroundColor: colors.gray200,
      borderRadius: 2,
      marginHorizontal: 12,
      flex: 1,
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.primary,
      borderRadius: 2,
    },
    attendeesQuestionContainer: {
      marginBottom: 20,
    },
    headerBanner: {
      backgroundColor: colors.white,
      padding: 16,
      borderRadius: 16,
      marginBottom: 20,
      borderWidth: 1,
      borderColor: colors.border + '50',
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: 2},
      shadowOpacity: 0.05,
      shadowRadius: 10,
      elevation: 2,
    },
    headerBannerText: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.textPrimary,
      lineHeight: 22,
    },
    attendeeCard: {
      backgroundColor: colors.white,
      borderRadius: 20,
      padding: 16,
      marginBottom: 16,
      borderWidth: 1,
      borderColor: colors.border + '30',
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: 4},
      shadowOpacity: 0.08,
      shadowRadius: 12,
      elevation: 3,
    },
    attendeeInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 20,
    },
    attendeeAvatar: {
      width: 50,
      height: 50,
      borderRadius: 25,
      marginRight: 12,
    },
    attendeeDetails: {
      flex: 1,
    },
    attendeeName: {
      fontSize: 18,
      fontWeight: '700',
      marginBottom: 4,
    },
    attendeeSubInfo: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    socialPulseText: {
      fontSize: 13,
      color: colors.textSecondary,
    },
    ratingButtonsContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    ratingOption: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: 10,
      marginHorizontal: 4,
      borderRadius: 12,
      borderWidth: 1,
    },
    ratingEmoji: {
      fontSize: 22,
      marginBottom: 4,
    },
    ratingLabel: {
      fontSize: 10,
      fontWeight: '600',
      textAlign: 'center',
    },
    didntShowButton: {
      paddingVertical: 12,
      borderRadius: 12,
      alignItems: 'center',
    },
    didntShowText: {
      fontSize: 14,
      fontWeight: '600',
    },
    questionContainer: {
      marginBottom: 28,
      backgroundColor: colors.cardBackground,
      padding: 16,
      borderRadius: 12,
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: 2},
      shadowOpacity: 0.06,
      shadowRadius: 4,
      elevation: 2,
    },
    questionText: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 14,
      lineHeight: 22,
      color: colors.textPrimary,
    },
    ratingBar: {
      alignSelf: 'flex-start',
      marginLeft: -8,
    },
    optionsContainer: {
      gap: 10,
    },
    optionButton: {
      padding: 14,
      borderRadius: 10,
      borderWidth: 1.5,
      transition: 'all 0.2s ease',
    },
    optionText: {
      fontSize: 15,
      fontWeight: '600',
      textAlign: 'center',
    },
    answerInput: {
      borderWidth: 1.5,
      borderColor: colors.border,
      borderRadius: 10,
      padding: 14,
      fontSize: 16,
      minHeight: 100,
      maxHeight: 120,
      textAlignVertical: 'top',
      backgroundColor: colors.white,
    },
    submitButton: {
      backgroundColor: colors.primary,
      paddingVertical: 16,
      borderRadius: 12,
      marginTop: 30,
      marginBottom: 30,
      shadowColor: colors.primary,
      shadowOffset: {width: 0, height: 4},
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
      flex: 1,
      marginLeft: 12,
    },
    submitButtonDisabled: {
      backgroundColor: colors.gray400,
      shadowOpacity: 0,
      elevation: 0,
    },
    submitButtonText: {
      color: colors.white,
      fontWeight: '700',
      textAlign: 'center',
      fontSize: 16,
    },
    completionText: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
      marginTop: 4,
    },
    scrollContent: {
      padding: 20,
      paddingTop: 0,
      paddingBottom: 40,
    },
    navigationContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 10,
      marginHorizontal: 20,
      marginTop: 'auto',
    },
    backButton: {
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.white,
    },
    backButtonText: {
      color: colors.textSecondary,
      fontWeight: '600',
      fontSize: 14,
    },
    nextButton: {
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 8,
      backgroundColor: colors.primary,
      flex: 1,
      marginLeft: 12,
    },
    nextButtonDisabled: {
      backgroundColor: colors.gray400,
    },
    nextButtonText: {
      color: colors.white,
      fontWeight: '600',
      fontSize: 14,
      textAlign: 'center',
    },
  });

  return (
    <BottomSheet
      ref={bottomSheetRef}
      snapPoints={snapPoints}
      index={-1}
      handleIndicatorStyle={{width: 70, backgroundColor: colors.gray400}}
      handleStyle={{
        backgroundColor: colors.background,
        borderTopRightRadius: 20,
        borderTopLeftRadius: 20,
        paddingVertical: 8,
      }}
      backgroundStyle={{
        backgroundColor: colors.background,
        borderTopRightRadius: 20,
        borderTopLeftRadius: 20,
      }}
      onClose={onSheetClose}
      enablePanDownToClose={false}
      enableContentPanningGesture={true}>
      <BottomSheetView style={styles.headerContainer}>
        <Text style={styles.headerTitle}>Quick Feedback</Text>
        <View style={styles.progressContainer}>
          <Text style={styles.progressText}>Step {currentStep + 1}</Text>
          <View style={styles.progressBar}>
            <View 
              style={[
                styles.progressFill, 
                { width: `${((currentStep + 1) / questions.length) * 100}%` }
              ]} 
            />
          </View>
          <Text style={styles.progressText}>of {questions.length}</Text>
        </View>
        <Text style={styles.completionText}>{completedQuestions} completed</Text>
      </BottomSheetView>
      
      <BottomSheetScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        
        {questions.length > 0 && renderQuestion(questions[currentStep], currentStep)}
        
      </BottomSheetScrollView>
        <View style={styles.navigationContainer}>
          {currentStep > 0 && (
            <TouchableOpacity 
              onPress={() => setCurrentStep(currentStep - 1)}
              style={styles.backButton}>
              <Text style={styles.backButtonText}>Back</Text>
            </TouchableOpacity>
          )}
          
          {currentStep < questions.length - 1 ? (
            <TouchableOpacity 
              onPress={() => {
                if (isFormValid()) {
                  setCurrentStep(currentStep + 1);
                }
              }}
              style={[
                styles.nextButton,
                !isFormValid() && styles.nextButtonDisabled
              ]}
              disabled={!isFormValid()}>
              <Text style={styles.nextButtonText}>Next</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity 
              onPress={onSubmitPress} 
              style={[
                styles.submitButton,
                !isFormValid() && styles.submitButtonDisabled
              ]}
              disabled={!isFormValid()}>
              <Text style={styles.submitButtonText}>Submit Review</Text>
            </TouchableOpacity>
          )}
        </View>
    </BottomSheet>
  );
};

export default FlexibleReviewSheet;